import { useWorkingPlatform } from '@repo/platform';
import { PageViewEventLogger, useUserEventLogger } from '@repo/tracking-service';
import { useDialog } from '@repo/ui';
import { AuthGuard } from '@repo/user-session';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useMount } from 'react-use';

import { SectionScrollModelProvider } from '@/components/common/context/SectionScrollModelProvider';
import Layout from '@/components/layout/Layout';
import { LookupRefundRequestContent } from '@/components/tax-refund/lookup/request/LookupRefundRequestContent';
import { COMMON_PATHS } from '@/lib/constants/paths';
import { BusinessType } from '@/lib/types/request';

const LookupRequestPage = ({ businessType }: { businessType: BusinessType }) => {
  const router = useRouter();
  const [isHideExitButton, setIsHideExitButton] = useState(false);
  const { sendClickEvent, sendCustomActionEvent } = useUserEventLogger();
  const { workingPlatform } = useWorkingPlatform();
  const { showDialog } = useDialog();

  const handleClick = () => {
    const eventName = businessType === 'indis' ? 'indis-lookup_loading_close' : 'corps-lookup_loading_close';
    sendClickEvent(eventName);

    showDialog({
      title: '환급액을 다음에 확인하시겠어요?',
      description: '조회가 곧 완료돼요. 나가시더라도 다음에는 예상 환급액을 바로 보여드릴게요.',
      buttons: {
        primary: {
          text: '나가기',
          onClick: () => {
            const okEventName = businessType === 'indis' ? 'indis-lookup_loading_close-ok' : 'corps-lookup_loading_close-ok';
            sendClickEvent(okEventName);
            router.push(COMMON_PATHS.환급조회목록);
          }
        },
        secondary: {
          text: '계속하기'
        }
      }
    });
  };

  const handleShowNetworkDisconnection = (isShow: boolean) => {
    setIsHideExitButton(isShow);
  };

  useMount(() => {
    sendCustomActionEvent(
      'lookup_request',
      'loading',
      {
        business: businessType
      },
      {
        isUseAirbridge: true,
        isUseGoogleAnalytics: true
      }
    );
  });

  if (!businessType || (businessType !== 'indis' && businessType !== 'corps')) {
    return null;
  }

  const pageName = businessType === 'indis' ? 'indis-lookup_loading_start' : 'corps-lookup_loading_start';
  const shouldShowExitButton = businessType === 'indis' ? !isHideExitButton && workingPlatform !== 'toss' : !isHideExitButton;

  return (
    <AuthGuard>
      <SectionScrollModelProvider>
        <PageViewEventLogger pageName={pageName} />
        <Layout
          rightTextButton={
            shouldShowExitButton
              ? {
                  label: '나가기',
                  state: 'gray',
                  onClick: handleClick
                }
              : undefined
          }
        >
          <LookupRefundRequestContent businessType={businessType} handleShowNetworkDisconnection={handleShowNetworkDisconnection} />
        </Layout>
      </SectionScrollModelProvider>
    </AuthGuard>
  );
};

export const getServerSideProps = (async ({ query }) => {
  if (query?.type && (query.type === 'indis' || query.type === 'corps')) {
    return { props: { businessType: query.type as BusinessType } };
  }

  return { props: { businessType: 'indis' as BusinessType } };
}) satisfies GetServerSideProps;

export default LookupRequestPage;
